import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة
import { 
  Save, Search, Gauge, List, Filter, Download, Maximize2, 
  History, Info, Activity, ChevronRight, Settings, Trash2, 
  MapPin, Paperclip, ArrowLeft, ChevronLeft, Type, Minus, Plus
} from 'lucide-react';
import UniversalLookup from './UniversalLookup';
import AttachmentsModal from './AttachmentsModal';
import AuditHistoryModal from './AuditHistoryModal';
import { useFontSize } from './FontSizeContext';
import { usePermissions } from './usePermissions'; // افترضت وجود الصلاحيات

// --- 🎯 تمرير isRTL و t للـ Component المساعد ---
const WhereUsedTable = ({ title, icon, data, columns, onRowClick, isRTL, t }) => (
  <div className="bg-surface-card border border-line rounded shadow-sm overflow-hidden">
    <div className={`p-2 bg-surface-panel border-b border-line font-black text-ink-secondary uppercase text-[0.69em] tracking-widest flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
      <div className={`flex items-center gap-2 italic ${isRTL ? 'flex-row-reverse' : ''}`}>
        {icon} {title}
      </div>
      <span className="bg-primary-soft text-primary px-2 py-0.5 rounded-full text-[0.62em]">{data?.length || 0} {t('common.records', 'Records')}</span>
    </div>
    <div className="overflow-x-auto">
      <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
        <thead className="bg-surface-panel text-ink-secondary text-[0.69em] uppercase font-black border-b border-line">
          <tr>
            {columns.map(col => <th key={col.key} className="p-2 border-x border-line-subtle">{col.label}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-line-subtle text-[0.77em]">
          {data?.length > 0 ? data.map((row, idx) => (
            <tr key={idx} onClick={() => onRowClick(row)} className="hover:bg-primary-soft cursor-pointer transition-colors group">
              {columns.map(col => (
                <td key={col.key} className="p-2 border-x border-line-subtle font-bold text-primary group-hover:text-primary-hover">
                  {row[col.key] || '---'}
                </td>
              ))}
            </tr>
          )) : (
            <tr>
              <td colSpan={columns.length} className="p-4 text-center text-ink-secondary italic uppercase tracking-tighter">
                {t('meters.where_used.no_assoc', 'No associations found in this category.')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const MeterMaster = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canCreate, canUpdate, canDelete } = usePermissions('meters');
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  
  const [activeTab, setActiveTab] = useState('Meter');
  const [fieldMaps, setFieldMaps] = useState({});
  const [whereUsed, setWhereUsed] = useState({ assets: [], locations: [], items: [] });
  const [loadingWhereUsed, setLoadingWhereUsed] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [attachCount, setAttachCount] = useState(0);
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
  const [stats, setStats] = useState({ last_reading: 0, last_reading_date: 'N/A', total_rollovers: 0 });

  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const [meter, setMeter] = useState({
    meter_num: '', meter_name: '', description: '', meter_type: 'CONTINUOUS',
    unit_of_measure: '', active: true, remarks: ''
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', domainId: '', field: '' });

  // 1. محرك النافيجيشن
  const loadNavigation = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await axios.get(`/api/meters/${id}/navigation${location.search}`);
      setNav(res.data);
    } catch (err) { console.error("Nav load failed"); }
  }, [id, isNew, location.search]);

  // 2. جلب المرفقات
  const fetchAttachCount = useCallback(async () => {
    if (isNew || !meter.meter_num) return;
    try {
      const res = await axios.get(`/api/attachments/count/METERS/${meter.meter_num}`);
      setAttachCount(res.data.count || 0);
    } catch (e) { console.error(e); }
  }, [isNew, meter.meter_num]);

  // 3. جلب بيانات الاستخدام
  const fetchWhereUsed = async () => {
    setLoadingWhereUsed(true);
    try {
      const res = await axios.get(`/api/meters/${id}/where-used`);
      setWhereUsed(res.data);
    } catch (err) { console.error("Error fetching where used data:", err); }
    finally { setLoadingWhereUsed(false); }
  };
  
  const fetchMeterStats = useCallback(async () => {
    if (isNew) return;
    try {
        const res = await axios.get(`/api/meters/${id}/statistics`);
        setStats(res.data);
    } catch (err) { console.error("Stats load failed"); }
  }, [id, isNew]);

  useEffect(() => {
      if (!isNew) fetchMeterStats();
  }, [fetchMeterStats, isNew]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const maps = await axios.get('/api/admin/field-map/Meters');
        setFieldMaps(maps.data || {});

        if (!isNew) {
          const res = await axios.get(`/api/meters/${id}`);
          setMeter(res.data);
          loadNavigation();
          fetchAttachCount();
        }
      } catch (err) { console.error("Error loading meter data:", err); }
    };
    loadData();
  }, [id, isNew, loadNavigation, fetchAttachCount]);

  useEffect(() => {
    if (activeTab === 'Where Used' && !isNew) {
      fetchWhereUsed();
    }
  }, [activeTab, isNew]);

  const handleSmartSearch = (fieldName, title, defaultType) => {
    const linkedDomain = fieldMaps[fieldName];
    setLookup({ 
      isOpen: true, title, 
      type: linkedDomain ? 'DOMAIN' : defaultType, 
      domainId: linkedDomain || '', 
      field: fieldName 
    });
  };

  const handleSave = async () => {
    const loadId = toast.loading(t('common.saving', "Saving..."));
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/meters/save', meter, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      toast.success(t('common.save_success', "Saved Successfully!"), { id: loadId });
      // 🎯 الحفاظ على الكويري بعد الحفظ (لو كان عنصر جديد)
      if (isNew) navigate(`/meters/${meter.meter_num}${location.search}`);
    } catch (err) { 
      toast.error(t('common.save_fail', "Save Failed: ") + (err.response?.data?.detail || err.message), { id: loadId }); 
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: t('common.are_you_sure', 'Are you sure?'),
      text: t('meters.details.delete_warn', { meter_num: meter.meter_num }),
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#3085d6',
      confirmButtonText: t('common.yes_delete', 'Yes, delete it!'),
      cancelButtonText: t('common.cancel', 'Cancel'),
      customClass: { popup: `${isRTL ? 'font-cairo' : 'font-sans'} text-[0.92em]` }
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/api/meters/${meter.meter_num}`, { headers: { 'Authorization': `Bearer ${token}` } });
        toast.success(t('common.delete_success', "Deleted successfully"));
        navigate(`/meters${location.search}`); // 🎯 الرجوع بالكويري
      } catch (err) {
        toast.error(t('common.delete_fail', "Delete failed: ") + (err.response?.data?.detail || "Unknown error"));
      }
    }
  };

  const goTo = (targetId) => targetId && navigate(`/meters/${targetId}${location.search}`);

  useEffect(() => {
    const triggerDelete = () => handleDelete();
    window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
  }, [handleDelete]);

  useEffect(() => {
    const openLog = () => setShowAudit(true);
    window.addEventListener('GLOBAL_LOG_HANDLER', openLog);
    return () => window.removeEventListener('GLOBAL_LOG_HANDLER', openLog);
  }, []);

  return (
    <div className={`flex flex-col h-screen bg-surface-app uppercase tracking-tighter overflow-hidden transition-[font-size] duration-300 ${isRTL ? 'font-cairo' : 'font-sans'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      <UniversalLookup {...lookup} onClose={() => setLookup({...lookup, isOpen: false})} 
        onSelect={(row) => {
          const val = row.value || row.id || row.unit_of_measure;
          setMeter(prev => ({ ...prev, [lookup.field]: val }));
          setLookup(prev => ({ ...prev, isOpen: false }));
        }} 
      />

      <AttachmentsModal 
        isOpen={isDocModalOpen} 
        onClose={() => {setIsDocModalOpen(false); fetchAttachCount();}} 
        ownertable="METERS"
        ownerid={meter.meter_num}
      />
      <AuditHistoryModal isOpen={showAudit} onClose={() => setShowAudit(false)} tableName="meters" recordId={meter.meter_num} />

      {/* --- 1. TOP TOOLBAR (Control Center) --- */}
      <div className={`bg-primary-dark text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4`}>
        <div className={`flex items-center gap-3 ${isRTL ? 'mr-2' : 'ml-2'}`}>
          {/* 🎯 زرار الرجوع السحري اللي بيحفظ الفلتر */}
          <button onClick={() => navigate(`/meters${location.search}`)} className="p-1.5 hover:bg-white/10 rounded-full text-white/70 transition-all">
            {isRTL ? <ChevronRight size={18} /> : <ArrowLeft size={18} />}
          </button>

          {!isNew && (
            <div className="flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1">
              <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <div className="px-2 text-[0.77em] font-mono border-x border-white/10 mx-1">
                <span className="text-status-warning font-black">{nav.current_pos}</span> / {nav.total}
              </div>
              <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
          )}
        </div>

        <div className={`flex items-center gap-2 ${isRTL ? 'ml-2' : 'mr-2'}`}>
          {(isNew ? canCreate : canUpdate) && (
            <button onClick={handleSave} className="bg-status-success hover:bg-status-success text-white px-4 py-1.5 rounded-sm flex items-center gap-2 text-[0.85em] font-black shadow-md transition-all uppercase">
              <Save size={14} /> {t('common.save', 'SAVE')}
            </button>
          )}

          <div className="w-[1px] h-6 bg-white/10 mx-1" />

          <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 hover:bg-white/10 rounded text-white/70">
            <Paperclip size={16} />
            {attachCount > 0 && <span className="absolute top-1 right-1 bg-status-danger text-[0.62em] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black animate-pulse">{attachCount}</span>}
          </button>

          {!isNew && canDelete && (
            <button onClick={handleDelete} className="p-2 hover:bg-status-danger-bg rounded text-status-danger transition-colors" title={t('common.delete', 'Delete')}>
              <Trash2 size={16}/>
            </button>
          )}

          {/* Font Controls */}
          <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 ml-4">
            <button onClick={() => fontContext.changeFontSize(-1)} className="hover:text-status-warning"><Minus size={14} /></button>
            <div className="flex items-center gap-1 min-w-[40px] justify-center text-white/70">
              <Type size={14} /><span className="font-black text-[0.77em]">{fontSize}</span>
            </div>
            <button onClick={() => fontContext.changeFontSize(1)} className="hover:text-status-warning"><Plus size={14} /></button>
          </div>
        </div>
      </div>

      {/* --- 2. HEADER BAR --- */}
      <div className={`bg-surface-card p-3 border-b border-line flex items-center gap-10 shadow-sm z-20`}>
        <div className={`flex flex-col min-w-[120px] ${isRTL ? 'mr-4' : 'ml-4'}`}>
          <label className="text-[0.69em] text-ink-secondary font-black uppercase tracking-widest">{t('meters.fields.meter_num', 'METER NUMBER')}</label>
          <input
            className={`text-sm font-black text-primary-dark outline-none border-b border-transparent focus:border-primary bg-transparent uppercase ${!isNew && 'opacity-60'}`}
            value={meter.meter_num}
            disabled={!isNew}
            onChange={e => setMeter({...meter, meter_num: e.target.value.toUpperCase()})}
            placeholder="NEW_METER"
          />
        </div>
        <div className={`flex flex-col flex-1 border-line ${isRTL ? 'border-r pr-8' : 'border-l pl-8'}`}>
          <label className="text-[0.69em] text-ink-secondary font-black uppercase tracking-widest">{t('assets.headers.desc', 'DESCRIPTION')}</label>
          <input
            className="text-sm font-bold text-ink outline-none focus:text-primary bg-transparent w-full transition-all"
            value={meter.description}
            onChange={e => setMeter({...meter, description: e.target.value})}
            placeholder={t('assets.headers.desc', 'Enter description...') + "..."}
          />
        </div>
      </div>

      {/* --- 3. TABS BAR --- */}
      <div className="flex bg-surface-card border-b border-line-subtle px-4 overflow-x-auto no-scrollbar shadow-inner">
        {[
          { id: 'Meter', label: t('meters.tabs.meter', 'Meter Details'), icon: <Gauge size={13}/> },
          { id: 'Where Used', label: t('meters.tabs.where_used', 'Where Used'), icon: <List size={13}/> }
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-8 py-3 flex items-center gap-2 transition-all whitespace-nowrap text-[0.77em] font-black uppercase tracking-tighter ${isRTL ? 'border-l border-line-subtle' : 'border-r border-line-subtle'}
            ${activeTab === t.id ? 'border-b-2 border-primary-dark text-primary-dark bg-primary-soft/30' : 'text-ink-secondary hover:text-ink'}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* --- 4. CONTENT AREA --- */}
      <div className="flex-1 overflow-y-auto p-4 custom-scrollbar bg-surface-panel">
        
        {activeTab === 'Meter' && (
          <div className="grid grid-cols-12 gap-6 max-w-7xl mx-auto animate-in fade-in duration-500">
            <div className="col-span-8 space-y-6">
              <SectionCard title={t('meters.sections.info', 'Meter Information')} isRTL={isRTL}>
                <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                  <EditableField label={t('meters.fields.meter_name', 'Meter Name')} value={meter.meter_name} onChange={v => setMeter({...meter, meter_name: v})} isRequired />
                  <DataField label={t('meters.fields.type', 'Meter Type')} value={meter.meter_type} onSearch={() => handleSmartSearch('meter_type', t('meters.fields.type'), 'DOMAIN')} />
                  <DataField label={t('meters.fields.uom', 'Unit of Measure')} value={meter.unit_of_measure} onSearch={() => handleSmartSearch('unit_of_measure', t('meters.fields.uom'), 'DOMAIN')} />
                  <EditableField
                    label={t('meters.fields.max_threshold', 'Default Critical Threshold')}
                    value={meter.max_threshold}
                    onChange={v => setMeter({...meter, max_threshold: v === '' ? null : v})}
                  />
                  
                  <div className={`flex items-center gap-2 pt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <input 
                      type="checkbox" 
                      checked={meter.active} 
                      onChange={(e) => setMeter({...meter, active: e.target.checked})} 
                      className="w-4 h-4 rounded accent-primary cursor-pointer"
                    />
                    <span className="font-bold text-primary uppercase">{t('meters.fields.active', 'Active?')}</span>
                  </div>
                </div>
                <p className="text-[0.75em] text-ink-secondary italic font-bold normal-case mt-3 pt-3 border-t border-line-subtle">
                  {t('meters.fields.max_threshold_hint', 'Used as the default failure threshold for AI predictive analysis (RUL) across all assets using this meter, unless a specific asset overrides it on its own Meters tab.')}
                </p>
              </SectionCard>

              <SectionCard title={t('meters.sections.additional', 'Additional Details')} isRTL={isRTL}>
                <div className="flex flex-col gap-1">
                  <label className="text-ink-secondary font-bold text-[0.69em] uppercase tracking-tighter">{t('meters.fields.remarks', 'Remarks / Notes')}</label>
                  <textarea
                    className={`border-b border-line outline-none h-24 resize-none text-[0.92em] focus:border-primary bg-transparent transition-all ${isRTL ? 'text-right' : ''}`}
                    value={meter.remarks || ''}
                    onChange={e => setMeter({...meter, remarks: e.target.value})}
                  />
                </div>
              </SectionCard>
            </div>

            <div className="col-span-4 space-y-6">
              <SectionCard title={t('meters.sections.stats', 'Meter Statistics')} isRTL={isRTL}>
                  <div className="text-[0.77em] space-y-3 text-ink-secondary font-bold">
                      <p className="flex justify-between border-b border-line pb-1">
                          {t('meters.fields.last_reading', 'LAST READING')}: <span className="text-primary font-black">{stats.last_reading?.toFixed(2)}</span>
                      </p>
                      <p className="flex justify-between border-b border-line pb-1">
                          {t('meters.fields.last_date', 'LAST READING DATE')}: <span className="text-primary font-mono">{stats.last_reading_date}</span>
                      </p>
                      <p className="flex justify-between border-b border-line pb-1">
                          {t('meters.fields.rollovers', 'TOTAL ROLLOVERS')}: <span className="text-primary font-black">{stats.total_rollovers}</span>
                      </p>
                  </div>
              </SectionCard>

            </div>
          </div>
        )}

        {activeTab === 'Where Used' && (
          <div className="flex flex-col gap-6 max-w-7xl mx-auto animate-in slide-in-from-right duration-500 pb-10">
            {loadingWhereUsed ? (
              <div className="text-center py-20 italic text-ink-secondary font-bold uppercase animate-pulse tracking-widest">
                {t('meters.where_used.searching', '🔍 Searching across the system...')}
              </div>
            ) : (
              <>
                <WhereUsedTable
                  title={t('meters.where_used.linked_assets', 'Linked Assets')}
                  icon={<Activity size={14} className="text-status-warning" />}
                  data={whereUsed.assets}
                  isRTL={isRTL} t={t}
                  columns={[
                    { label: t('meters.where_used.asset_num', 'Asset Num'), key: 'assetnum' },
                    { label: t('assets.headers.desc', 'Description'), key: 'description' },
                    { label: t('meters.where_used.site', 'Site'), key: 'site_id' },
                    { label: t('meters.where_used.status', 'Status'), key: 'status' }
                  ]}
                  onRowClick={(row) => navigate(`/assets/${row.asset_id}`)}
                />

                <WhereUsedTable 
                  title={t('meters.where_used.linked_locations', 'Linked Locations')} 
                  icon={<MapPin size={14} className="text-primary" />}
                  data={whereUsed.locations}
                  isRTL={isRTL} t={t}
                  columns={[
                    { label: t('meters.where_used.location', 'Location'), key: 'location_id' },
                    { label: t('assets.headers.desc', 'Description'), key: 'description' },
                    { label: t('meters.fields.type', 'Type'), key: 'type' },
                    { label: t('meters.where_used.status', 'Status'), key: 'status' }
                  ]}
                  onRowClick={(row) => navigate(`/locations/${row.location_id}`)}
                />

                <WhereUsedTable 
                  title={t('meters.where_used.linked_items', 'Linked Items')} 
                  icon={<Settings size={14} className="text-status-success" />}
                  data={whereUsed.items}
                  isRTL={isRTL} t={t}
                  columns={[
                    { label: t('meters.where_used.item_num', 'Item Num'), key: 'itemnum' },
                    { label: t('assets.headers.desc', 'Description'), key: 'description' },
                    { label: t('meters.where_used.category', 'Category'), key: 'category' }
                  ]}
                  onRowClick={(row) => navigate(`/inventory/${row.itemnum}`)}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// --- Reusable Shared Helpers (Visual Consistency) ---

const SectionCard = ({ title, children, isRTL }) => (
  <div className="bg-surface-card border border-line rounded shadow-sm overflow-hidden">
    <div className={`p-2 bg-surface-panel border-b border-line font-black text-ink-secondary uppercase text-[0.9em] tracking-widest flex items-center gap-2 italic `}>
      <div className="w-1.5 h-3 bg-primary-dark rounded-full"></div> {title}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const DataField = ({ label, value, onSearch, isHighlight }) => (
  <div className="flex flex-col border-b border-line-subtle py-1.5 group hover:bg-primary-soft/30 transition-all rounded px-1">
    <label className="text-[0.9em] font-bold text-ink-secondary uppercase tracking-tighter">{label}</label>
    <div className="flex justify-between items-center h-5">
      <span className={`font-bold uppercase truncate ${isHighlight ? 'text-status-success text-sm' : 'text-primary'}`}>{value || '---'}</span>
      {onSearch && <Search size={13} className="text-primary-hover cursor-pointer hover:text-primary transition-colors hover:scale-110" onClick={onSearch} />}
    </div>
  </div>
);

const EditableField = ({ label, value, onChange, onSearch, isRequired }) => (
  <div className="flex flex-col gap-1 border-b border-line group">
    <label className={`${isRequired ? 'text-status-danger' : 'text-ink-secondary'} font-bold text-[0.9em] uppercase tracking-tighter`}>{label}</label>
    <div className="flex items-center justify-between pb-1">
      <input className="outline-none font-bold text-primary bg-transparent w-full" value={value || ''} onChange={e => onChange && onChange(e.target.value)} />
      {onSearch && <Search size={13} className="text-primary-hover cursor-pointer hover:text-primary transition-colors hover:scale-110" onClick={onSearch} />}
    </div>
  </div>
);

export default MeterMaster;